import { Component, OnInit } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ProductName } from 'situview-webapi-client';
import { AuthService, ConfirmDialogService, StateService } from 'src/app/services';
import { ConfigService } from 'src/app/services/config.service';
import { SituReel, SituReelItem, SituReelService } from 'src/app/services/situ-reel.service';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { SituReelEditDialogComponent, SituReelEditDialogData } from '../situ-reel-edit-dialog/situ-reel-edit-dialog.component';

@Component({
  selector: 'situ-reels-list',
  templateUrl: './situ-reels-list.component.html',
  styleUrls: ['./situ-reels-list.component.scss']
})
export class SituReelsListComponent extends SituBaseComponent implements OnInit {
  reels: SituReel[] = [];
  loading = false;
  thumbnailFailed: { [artifactId: string]: boolean } = {};

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private dialogService: ConfirmDialogService,
    private clipboard: Clipboard,
    private reelService: SituReelService,
    private configService: ConfigService) {
    super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this._subscriptions.push(
      this.reelService.reelsChanged$.subscribe(reels => this.reels = reels)
    );
    this.refresh();
  }

  protected onSelectedCompanyChange(): void {
    this.refresh();
  }

  refresh(): void {
    if (!this.selectedCompany?.id) {
      this.reels = [];
      return;
    }
    this.loading = true;
    this.reelService.listReels(this.selectedCompany.id)
      .then(reels => this.reels = reels)
      .catch(() => this.snackBar.open('Could not load SituReels.', 'OK', { duration: 3000 }))
      .finally(() => this.loading = false);
  }

  openCreate(): void {
    if (!this.selectedCompany?.id) return;
    this.dialog.open<SituReelEditDialogComponent, SituReelEditDialogData, SituReel>(
      SituReelEditDialogComponent,
      {
        data: { reel: null, companyId: this.selectedCompany.id },
        width: '1100px',
        maxWidth: '95vw',
        autoFocus: false,
        panelClass: 'situ-reel-edit-dialog'
      });
  }

  openEdit(reel: SituReel): void {
    if (!this.selectedCompany?.id) return;
    this.dialog.open<SituReelEditDialogComponent, SituReelEditDialogData, SituReel>(
      SituReelEditDialogComponent,
      {
        data: { reel, companyId: this.selectedCompany.id },
        width: '1100px',
        maxWidth: '95vw',
        autoFocus: false,
        panelClass: 'situ-reel-edit-dialog'
      });
  }

  confirmDelete(reel: SituReel): void {
    if (!this.selectedCompany?.id) return;
    const companyId = this.selectedCompany.id;
    const options: ConfirmDialogData = {
      title: 'Delete SituReel',
      message: `Delete "${reel.name}"? This will break any existing share links to this reel.`,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'delete',
      actionColour: 'warn'
    };
    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (!confirmed) return;
      this.reelService.deleteReel(reel.id, companyId)
        .then(() => this.snackBar.open('Reel deleted.', 'OK', { duration: 2000 }))
        .catch(() => this.snackBar.open('Could not delete reel.', 'OK', { duration: 3000 }));
    }));
  }

  copyShareUrl(reel: SituReel): void {
    const url = this.shareUrl(reel);
    if (!url) return;
    this.clipboard.copy(url);
    this.snackBar.open('Share URL copied!', 'OK', { duration: 1500 });
  }

  openInNewTab(reel: SituReel): void {
    const url = this.shareUrl(reel);
    if (url) window.open(url, '_blank');
  }

  shareUrl(reel: SituReel): string {
    const base = this.configService.config.situLinkBaseUrl
      ? `https://reel.${this.configService.config.situLinkBaseUrl}`
      : this.configService.config.situPortalBaseUrl;
    return `${base}/${reel.id}`;
  }

  defaultItem(reel: SituReel): SituReelItem | undefined {
    return reel.items.find(i => i.artifactId === reel.defaultArtifactId) ?? reel.items[0];
  }

  otherItems(reel: SituReel): SituReelItem[] {
    const def = this.defaultItem(reel);
    return reel.items.filter(i => i !== def);
  }

  visibleOtherItems(reel: SituReel): SituReelItem[] {
    return this.otherItems(reel).slice(0, 4);
  }

  hiddenOtherCount(reel: SituReel): number {
    return Math.max(0, this.otherItems(reel).length - 4);
  }

  getItemThumb(item: SituReelItem): string {
    if (item.thumbnailOverrideUrl) return item.thumbnailOverrideUrl;
    const base = this.configService.config.situViewWebApiBaseUrl;
    const id = item.thumbnailArtifactId || item.artifactId;
    if (item.productName === ProductName.Facade) {
      return `${base}/home/GetImage?artifactId=${id}&fileName=facade.jpg&width=512`;
    }
    if (item.productName === ProductName.ProductPreview) {
      return `${base}/home/GetImage?artifactId=${id}&fileName=preview.webp`;
    }
    return `${base}/home/GetThumb?artifactId=${id}`;
  }

  onThumbError(item: SituReelItem): void {
    this.thumbnailFailed[item.artifactId] = true;
  }

  getFallbackIcon(item: SituReelItem): string {
    switch (item.productName) {
      case ProductName.Facade: return 'home';
      case ProductName.Plan: return 'map';
      case ProductName.Render: return 'image';
      case ProductName.ProductPreview: return 'view_in_ar';
      default: return 'panorama';
    }
  }

  trackById(_index: number, reel: SituReel): string {
    return reel.id;
  }
}
